import type { BirdieAPI, BirdieSettings } from '@birdie-so/snippet'

interface BirdieWindow extends Window {
  birdie?: BirdieAPI
  birdieSettings?: Partial<BirdieSettings> & {
    onBirdieReady?: () => void
  }
}

const birdieSnippetId = 'birdie-snippet'
const birdieSnippetBaseUrl = 'https://app.birdie.so/widget/embed'
const serviceWorkerBypassParam = 'ngsw-bypass'
let readyCallbacks: ((birdie: BirdieAPI) => void)[] = []

/**
 * Initializes Birdie with a snippet URL that bypasses Angular's service worker.
 * @param {BirdieSettings} settings Birdie initialization settings.
 */
export function initBirdieSnippet(settings: BirdieSettings): void {
  const { clientId, metadata, onReady, ...otherSettings } = settings

  if (!clientId) {
    console.error('[Birdie] Missing required clientId in initBirdieSnippet()')

    return
  }

  if (typeof window === 'undefined') {
    return
  }

  const birdieWindow = window as BirdieWindow

  birdieWindow.birdieSettings ??= {}
  birdieWindow.birdieSettings.metadata = metadata
  Object.assign(birdieWindow.birdieSettings, otherSettings)

  birdieWindow.birdieSettings.onBirdieReady = () => {
    const birdie = birdieWindow.birdie

    if (!birdie) {
      return
    }

    readyCallbacks.forEach(callback => callback(birdie))
    readyCallbacks = []
  }

  if (onReady) {
    registerBirdieReadyCallback(onReady)
  }

  if (!document.getElementById(birdieSnippetId)) {
    const script = document.createElement('script')

    // Birdie's redirect lacks CORS headers, so use the default script request mode.
    script.src = buildBirdieSnippetUrl(clientId)
    script.async = true
    script.id = birdieSnippetId
    document.body.appendChild(script)
  }
}

/**
 * Builds the Birdie snippet URL with Angular service worker bypass enabled.
 * @param {string} clientId Birdie client id.
 * @returns {string} Birdie snippet URL.
 */
export function buildBirdieSnippetUrl(clientId: string): string {
  const url = new URL(`${birdieSnippetBaseUrl}/${clientId}`)

  url.searchParams.set(serviceWorkerBypassParam, 'true')

  return url.toString()
}

/**
 * Resets module state for isolated tests.
 */
export function resetBirdieSnippetForTests(): void {
  readyCallbacks = []
}

function registerBirdieReadyCallback(callback: (birdie: BirdieAPI) => void): void {
  const birdieWindow = window as BirdieWindow

  if (birdieWindow.birdie) {
    callback(birdieWindow.birdie)

    return
  }

  readyCallbacks.push(callback)
}
